"use client";

import { TextType, Typography, TypographyProps } from "@components/atoms/typography/typography";
import { LegalCaption, LegalSubCaption } from "@components/molecules/legal/legalTypography";
import { EmailLink } from "@components/EmailLink";
import React from "react";
import { getLegalProfileByCountry } from "@features/shared/legal/company";
import { bidiIsolate } from "@features/helpers";
import { useUserCountryCode } from "@features/shared/legal/useUserCountryCode";
import { useT } from "@features/hooks";

function Paragraph(p: Omit<TypographyProps, "type">) {
    return <Typography type={TextType.PARAGRAPH} {...p} />;
}

const Page = () => {
    const t = useT();
    const { countryCode, isLoading } = useUserCountryCode();
    const legalCompanyInfo = getLegalProfileByCountry(countryCode);
    const { companyRegistrationText, personalDataControllerText } = legalCompanyInfo;

    if (isLoading) {
        return null;
    }

    return (
        <div className="py-10">
            <LegalCaption>{t("Privacy Policy")}</LegalCaption>

            <Paragraph position="left" marginBottom="20px">
                {t(
                    "The Service is provided by {{companyName}}, {{companyRegistrationText}}, with its registered address at {{address}}. The company is committed to protecting your privacy and ensuring the secure and responsible handling of your personal data. This Privacy Policy describes how the company collects, uses, and protects your information when you use our web application Tixu (the \"Service\") or the Tixu app in the App Store, Google Play (the \"App\").",
                    {
                        variables: {
                            companyName: bidiIsolate(legalCompanyInfo.companyName),
                            companyRegistrationText,
                            address: bidiIsolate(legalCompanyInfo.address)
                        }
                    }
                )}
            </Paragraph>

            <LegalSubCaption position="left">{t("1. Personal Data Controller")}</LegalSubCaption>
            <Paragraph position="left" marginBottom="20px">
                {t(
                    "1.1. {{companyName}}, a legal entity{{personalDataControllerText}}{{address}} will be the controller of your personal data.",
                    {
                        variables: {
                            companyName: bidiIsolate(legalCompanyInfo.companyName),
                            personalDataControllerText,
                            address: bidiIsolate(legalCompanyInfo.address)
                        }
                    }
                )}
            </Paragraph>

            <LegalSubCaption position="left">{t("2. Information We Collect")}</LegalSubCaption>
            <Paragraph position="left" marginBottom="20px">
                {t("2.1. Data you give us:")}
                <ol className="list-decimal ms-6">
                    <li className="text-gray-500 mb-2">
                        {t(
                            "You provide us information about yourself when you register for and/or use the Service. For example: age, gender, data on financial status (including your questions regarding financial mindset), email address."
                        )}
                    </li>
                </ol>
                {t("2.2. Data we collect automatically:")}
                <ol className="list-decimal ms-6">
                    <li className="text-gray-500 mb-2">
                        {t(
                            "Data about how you found us: We collect data about your referring URL (that is, the place on the Web where you were when you tapped on our ad)."
                        )}
                    </li>
                    <li className="text-gray-500 mb-2">
                        {t(
                            "Device and Location data: We collect data about your referring URL (that is, the place on the Web where you were when you tapped on our ad)."
                        )}
                    </li>
                    <li className="text-gray-500 mb-2">
                        {t(
                            "Usage data: We record how you interact with our Service. For example, we log your taps/clicks on certain areas of the interface, the features, and content you interact with, how often you use the Service, how long you are in the Service, and your subscription orders. We also record the ads in our Website with which you interact (and the Internet links to which those adds lead)."
                        )}
                    </li>
                    <li className="text-gray-500 mb-2">
                        {t(
                            "Transaction data: When you make payments through the Service, you need to provide financial account data, such as your credit card number, to our third-party service providers. We do not collect or store full credit card number data, though we may receive credit card-related data, data about the transaction, including: date, time and amount of the transaction, the type of payment method used."
                        )}
                    </li>
                    <li className="text-gray-500 mb-2">
                        {t("Cookies: We collect data using cookies to improve the user experience.")}
                    </li>
                </ol>
            </Paragraph>

            <LegalSubCaption position="left">{t("3. How We Use Your Information")}</LegalSubCaption>
            <Paragraph position="left" marginBottom="20px">
                {t("3.1. The company uses the collected information to:")}
                <ol className="list-decimal ms-6">
                    <li className="text-gray-500 mb-2"> {t("Provide products and services and process return requests.")}</li>
                    <li className="text-gray-500 mb-2"> {t("Improve the company’s service offerings.")}</li>
                    <li className="text-gray-500 mb-2"> {t("Facilitate the order placement process.")}</li>
                    <li className="text-gray-500 mb-2"> {t("Conduct analyses.")}</li>
                    <li className="text-gray-500 mb-2">
                        {" "}
                        {t(
                            "Communicate with you, for example, to respond to your inquiries, requests, issue reports, feedback, and follow up on them."
                        )}
                    </li>
                    <li className="text-gray-500 mb-2">
                        {" "}
                        {t(
                            "Send marketing and promotional materials, including information about products, services, and sales."
                        )}
                    </li>
                </ol>
            </Paragraph>

            <LegalSubCaption position="left">{t("4. Sharing Your Information")}</LegalSubCaption>
            <Paragraph position="left" marginBottom="20px">
                {t(
                    "4.1. The company does not sell or share your personal information with third parties. However, the company may share your information with:"
                )}
                <ol className="list-decimal ms-6">
                    <li className="text-gray-500 mb-2">
                        {t(
                            "Disclosure With Your Consent: The company may disclose your information to third parties if it has received your consent or direction to do so."
                        )}
                    </li>
                    <li className="text-gray-500 mb-2">
                        {t(
                            "Service Providers: The company may transfer your information to service providers. Service providers assist with, among other things, administering the site, providing services for smooth business operations, offering technical support, processing payments, and assisting with order fulfillment."
                        )}
                    </li>
                    <li className="text-gray-500 mb-2">
                        {t(
                            "Disclosure Without Consent: The company may disclose information in response to a court order, subpoena, or court directive, as well as in connection with legal processes or to comply with relevant laws. The company may also share your information to establish and exercise its rights, defend against claims, investigate, prevent, and take action against potential illegal activities, suspected fraud, threats to personal safety or property, violations of policies, and to fulfill your request to ship products and provide services through a third-party intermediary."
                        )}
                    </li>
                </ol>
            </Paragraph>

            <LegalSubCaption position="left">{t("5. Security of Your Information")}</LegalSubCaption>
            <Paragraph position="left" marginBottom="20px">
                {t(
                    "5.1. The company implements appropriate technical and organizational measures to protect your personal information from unauthorized access, use, or disclosure."
                )}
            </Paragraph>

            <LegalSubCaption position="left">{t("6. Data Retention")}</LegalSubCaption>
            <Paragraph position="left" marginBottom="20px">
                {t(
                    "6.1. The company will retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy. After this period, your personal data will be deleted or anonymized. The company will also retain and use your personal data as necessary to comply with legal obligations, resolve disputes, and enforce its agreements."
                )}
            </Paragraph>

            <LegalSubCaption position="left">{t("7. Your Privacy Rights")}</LegalSubCaption>
            <Paragraph position="left" marginBottom="20px">
                {t("7.1. You have the right to:")}
                <ol className="list-decimal ms-6">
                    <li className="text-gray-500 mb-2"> {t("Access the personal data the company holds about you.")}</li>
                    <li className="text-gray-500 mb-2"> {t("Request correction or deletion of your personal data.")}</li>
                    <li className="text-gray-500 mb-2"> {t("Object to the processing of your personal data.")}</li>
                    <li className="text-gray-500 mb-2"> {t("Request data portability.")}</li>
                </ol>
            </Paragraph>

            <LegalSubCaption position="left">{t("8. California Privacy Rights")}</LegalSubCaption>
            <Paragraph position="left" marginBottom="20px">
                {t(
                    "8.1. This section provides additional details on how the company processes the personal data of California consumers and the rights available to them under the California Consumer Privacy Act (\"CCPA\") and California’s Shine the Light law. This section applies only to residents of California, United States. California's Shine the Light law provides California residents the right to ask companies once a year what personal information they share with third parties for those third parties' direct marketing purposes."
                )}
            </Paragraph>

            <LegalSubCaption position="left">{t("9. Changes to the Privacy Policy")}</LegalSubCaption>
            <Paragraph position="left" marginBottom="20px">
                {t(
                    "9.1. The company may update this Privacy Policy from time to time. All changes will be posted on this page with the updated effective date. You are encouraged to periodically review this policy for any changes."
                )}
            </Paragraph>

            <LegalSubCaption position="left">{t("Contact us")}</LegalSubCaption>

            <Paragraph position="left" marginBottom="20px">
                {t("If you have any questions or concerns regarding this Privacy Policy, please contact the company at:")}
                <ul className="list-decimal ms-6">
                    <li className="text-gray-500 mb-2">
                        {t("E-mail:")}{" "}
                        <EmailLink email="support@tixu.ai" className="text-gray-500">
                            support@tixu.ai
                        </EmailLink>
                    </li>
                    <li className="text-gray-500 mb-2">
                        {t("- Address: {{address}}", { variables: { address: bidiIsolate(legalCompanyInfo.address) } })}
                    </li>
                </ul>
            </Paragraph>
            <Paragraph position="left" marginBottom="20px">
                {t(
                    "By using the Service, you agree to the collection and use of your information in accordance with this Privacy Policy."
                )}
            </Paragraph>

            <Paragraph position="left" marginBottom="20px">
                {t("Effective Date: May 18, 2026")}
            </Paragraph>
        </div>
    );
};

export default Page;
